import React, { useEffect, useState, useCallback } from "react";
import { Sun, Loader2, Plus, Save, Trash2, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { text_en: "", text_bn: "", text_hi: "", attribution: "", published: true, order: 0 };
const inputCls =
  "w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60";

export default function DarshanPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/darshan");
      setItems(r.data || []);
    } catch { toast.error("Could not load darshan"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text_en.trim()) { toast.error("The English reflection is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/darshan/${editing}`, form);
        toast.success("Reflection updated");
      } else {
        await api.post("/admin/darshan", form);
        toast.success("Reflection added");
      }
      setForm(EMPTY); setEditing(null); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/darshan/${item.id}`, { published: !item.published });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/darshan/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const published = items.filter((i) => i.published).length;

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8" data-testid="darshan-panel">
      <form onSubmit={submit} className="glass-strong rounded-2xl p-6 space-y-4 h-fit lg:sticky lg:top-6" data-testid="darshan-form">
        <h3 className="font-serif text-lg font-bold text-tara-ink flex items-center gap-2">
          <Sun className="h-4 w-4 text-tara-gold" /> {editing ? "Edit reflection" : "Add a daily reflection"}
        </h3>
        <p className="text-xs text-tara-muted leading-relaxed">
          One published reflection is shown on the homepage each day, rotating through the list. {published} published.
        </p>
        <textarea value={form.text_en} onChange={(e) => setForm({ ...form, text_en: e.target.value })} rows={4} placeholder="Reflection — English *" className={inputCls} data-testid="darshan-text-en-input" />
        <textarea value={form.text_bn} onChange={(e) => setForm({ ...form, text_bn: e.target.value })} rows={3} placeholder="বাংলা" className={inputCls} data-testid="darshan-text-bn-input" />
        <textarea value={form.text_hi} onChange={(e) => setForm({ ...form, text_hi: e.target.value })} rows={3} placeholder="हिन्दी" className={inputCls} data-testid="darshan-text-hi-input" />
        <div className="flex items-center gap-4">
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} placeholder="Order" className={inputCls} data-testid="darshan-order-input" />
          <label className="flex items-center gap-2 text-sm text-tara-body whitespace-nowrap cursor-pointer">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#B4232A]" data-testid="darshan-published-checkbox" />
            Published
          </label>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="darshan-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editing ? "Save changes" : "Add reflection"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="btn-ghost-gold rounded-full px-5 py-3 text-sm font-semibold" data-testid="darshan-cancel-btn">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="darshan-empty">
            No reflections yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="darshan-list">
            {items.map((d) => (
              <div key={d.id} className={`glass rounded-2xl p-5 ${d.published ? "" : "opacity-60"}`} data-testid={`darshan-${d.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <p className="font-display text-lg text-tara-ink italic leading-relaxed">“{d.text_en}”</p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => toggle(d)} className="rounded-full border border-tara-gold/30 p-2 text-tara-goldDeep hover:bg-tara-gold/10 transition" data-testid={`darshan-toggle-${d.id}`} aria-label="Toggle publish">
                      {d.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => { setEditing(d.id); setForm({ ...EMPTY, ...d }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="rounded-full border border-tara-gold/30 px-3 py-2 text-xs font-semibold text-tara-goldDeep hover:bg-tara-gold/10 transition" data-testid={`darshan-edit-${d.id}`}>
                      Edit
                    </button>
                    <button onClick={() => remove(d.id)} className="rounded-full border border-red-500/40 p-2 text-red-600 hover:bg-red-500/10 transition" data-testid={`darshan-delete-${d.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {d.text_bn && <p className="text-sm text-tara-muted italic mt-2">“{d.text_bn}”</p>}
                {d.text_hi && <p className="text-sm text-tara-muted italic mt-1.5">“{d.text_hi}”</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
